/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useAppDispatch, useAppSelector } from "../../../../../../../redux/hooks"
import Dialog from "../../../../../../components/Dialogs/ArgosDialog"
import {
  closeDownloadDataSetDialog, selectDownloadDataSetDialog, selectGreenLineName
} from "../../../../../../../redux/ducks/SensingSystemArchitectureDesign"
import Flexbox from "../../../../../../components/Containers/ArgosFlexbox"
import Button from "../../../../../../components/Buttons/ArgosButton"
import {
  SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS as CONSTANTS
} from "../../../../constants"
import toast from "react-hot-toast"
import { config } from "../../../../../../../AppConfig/config"
import { loginRequest, requestIdToken } from "../../../../../../../auth/authConfig"
import { useMsal } from "@azure/msal-react"
import { useState, useEffect } from "react"
import TextField from "../../../../../../components/Form/TextField/ArgosTextField"
import Checkbox from "../../../../../../components/Form/Checkbox/ArgosCheckbox"
import { FormControl, FormControlLabel, FormGroup, FormLabel } from "@mui/material"
import Tooltip from '@mui/material/Tooltip'


/**
 * Dialog to confirm an action on a study
 */
export const DownloadDataSetDialog = () => {
  const dispatch = useAppDispatch()
  const {instance, accounts} = useMsal()

  const isOpen = useAppSelector(selectDownloadDataSetDialog).isOpen
  const dataSetId = useAppSelector(selectDownloadDataSetDialog).dataSetId
  const dataSetName = useAppSelector(state => selectGreenLineName(state, dataSetId ?? ''))
  const closeDialog = () => {dispatch(closeDownloadDataSetDialog())}

  const [fileName, setFileName] = useState<string>('')
  const [fileFormat, setFileFormat] = useState<string>('csv')
  const [includeGreenLine, setIncludeGreenLine] = useState<boolean>(false)
  const [includeRedLine, setIncludeRedLine] = useState<boolean>(false)
  const [includeRawData, setIncludeRawData] = useState<boolean>(false)
  const [includeBlueLine, setIncludeBlueLine] = useState<boolean>(false)
  const [includeViolations, setIncludeViolations] = useState<boolean>(false)

  useEffect(() => {
    if (dataSetName) {
      setFileName(dataSetName)  /** Update fileName when dataSetName changes */
    }
  }, [dataSetName])

  /**
   * Handle the download of the data set
   */
  const downloadDataSetUrl = CONSTANTS.viewStudy.endpoints.downloadDataSet
  const downloadDataSet = async () => {
    closeDialog()
    const body = {dataSetId, fileName, fileFormat, includeGreenLine, includeRedLine, includeRawData, includeBlueLine, includeViolations}
    const toastId = toast.loading('Downloading...')
    try {
      await handleDownloadDatasetFile(body)
      toast.success('Download complete!', { id: toastId, duration: 3000 })
    } catch (error: any) {
      toast.error(
        error.message,
        { id: toastId, duration: 3000 }
      )
    }
  }

  const handleDownloadDatasetFile = async (body: any) => {
    let url = `${config.url.API_URL}${downloadDataSetUrl}`
    const idTok = await requestIdToken(instance, accounts)
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idTok}`,
      },
      body: JSON.stringify(body)
    })

    if (response.status === 401) {
      await instance.loginRedirect(loginRequest)
      throw new Error('Unauthorized')
    }

    if (!response.ok) {
      const errorText = await response.text()
      console.log(`Error downloading data set: ${errorText}`)
      throw new Error(errorText)
    }

    const blob = await response.blob()
    const fileURL = window.URL.createObjectURL(blob)
    const alink = document.createElement('a')
    alink.href = fileURL
    alink.download = `${fileName}.${fileFormat}`
    alink.click()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={closeDialog}
      headerTitle='Download Data Set'
      height='fit-content'
      width='fit-content'
    >
      <Flexbox vertical style={{padding: '10px', gap: '20px'}}>
        <TextField
          label='File Name'
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
          variant='standard'
          fullWidth
          required
        />
        <FormControl component='fieldset' variant='standard'>
          <FormLabel component='legend'>Data to Include:</FormLabel>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox
                checked={includeGreenLine}
                onChange={(e) => {
                  setIncludeGreenLine(e.target.checked)
                  if (!e.target.checked) {
                    /** Red Line must be unchecked if Green Line is unchecked */
                    setIncludeRedLine(false)
                  }
                }}
              />}
              label='Green Line'
            />
            <Tooltip
              title='Green Line must be checked to include Red Line'
              disableHoverListener={includeGreenLine}  /** Show tooltip only when Green Line is unchecked */
            >
              <FormControlLabel
                control={<Checkbox
                  checked={includeRedLine}
                  onChange={(e) => {
                    setIncludeRedLine(e.target.checked)
                    if (!e.target.checked) {
                      /** Raw Data must be unchecked if Red Line is unchecked */
                      setIncludeRawData(false)
                    }
                  }}
                  disabled={!includeGreenLine}  /** Green Line must be checked to check Red Line */
                />}
                label='Red Line'
              />
            </Tooltip>
            <Tooltip
              title='Red Line must be checked to include Raw Data'
              disableHoverListener={includeRedLine}  /** Show tooltip only when Red Line is unchecked */
            >
              <FormControlLabel
                control={<Checkbox
                  checked={includeRawData}
                  onChange={(e) => setIncludeRawData(e.target.checked)}
                  disabled={!includeRedLine}  /** Red Line must be checked to check Raw Data */
                />}
                label='Raw Data'
              />
            </Tooltip>
            <FormControlLabel
              control={<Checkbox
                checked={includeBlueLine}
                onChange={(e) => setIncludeBlueLine(e.target.checked)}
                disabled={false}
              />}
              label='Blue Line'
            />
            <FormControlLabel
              control={<Checkbox
                checked={includeViolations}
                onChange={(e) => setIncludeViolations(e.target.checked)}
                disabled={false}
              />}
              label='Violations'
            />
          </FormGroup>
        </FormControl>
        <Flexbox style={{justifyContent: 'center'}}>
          <Button
            id='cancel-button'
            data-testid='cancel-button'
            variant='text'
            size='large'
            fontSize={16}
            onClick={closeDialog}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            id='download-button'
            data-testid='download-button'
            variant='contained'
            size='large'
            fontSize={16}
            onClick={downloadDataSet}
            fullWidth
            color='primary'
            disabled={!fileName || (!includeGreenLine && !includeBlueLine && !includeViolations)}  /** Disable button if file name is empty or if don't have proper selections */
          >
            Download
          </Button>
        </Flexbox>
      </Flexbox>
    </Dialog>
  )
}
